import { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Button } from './ui/button';
import { 
  Mic, MicOff, Volume2, VolumeX, X, Loader2, 
  Send, User, StopCircle, ChevronDown, ChevronUp,
  Sparkles, RotateCcw, Phone, PhoneOff
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../lib/utils';

import { API_BASE_URL as API } from '../utils/apiConfig';

// Voice Activity Detection config
const VAD_CONFIG = {
  silenceThreshold: -45,      // dB below which = silence
  silenceDuration: 2500,       // ms of silence before auto-submit
  minRecordDuration: 500,      // minimum ms to record before allowing submit
  promptTimeout: 8000,         // ms of complete silence before "still there?" prompt
};

export default function FundleCopilot({ 
  dashboardType = 'command-center',
  brandId = null,
  brandName = '',
  onNavigate = null,
  filters = {}
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [isVoiceMode, setIsVoiceMode] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');
  const [isExpanded, setIsExpanded] = useState(true);
  const [brandContext, setBrandContext] = useState('');
  const [contextLoading, setContextLoading] = useState(false);
  const [voiceLevel, setVoiceLevel] = useState(0);
  const [sessionId] = useState(() => `fc_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`);
  
  const audioRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const messagesEndRef = useRef(null);
  const prevBrandIdRef = useRef(null);
  
  // VAD refs
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const streamRef = useRef(null);
  const silenceTimerRef = useRef(null);
  const promptTimerRef = useRef(null);
  const recordStartRef = useRef(null);
  const vadFrameRef = useRef(null);
  const isVoiceModeRef = useRef(false);
  const isSpeakingRef = useRef(false);
  const isProcessingRef = useRef(false);
  const hasSpokenRef = useRef(false);

  // Keep refs in sync
  useEffect(() => { isVoiceModeRef.current = isVoiceMode; }, [isVoiceMode]);
  useEffect(() => { isSpeakingRef.current = isSpeaking; }, [isSpeaking]);
  useEffect(() => { isProcessingRef.current = isProcessing; }, [isProcessing]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Fetch brand context
  const fetchBrandContext = useCallback(async () => {
    if (!brandId) return;
    setContextLoading(true);
    try {
      const res = await fetch(`${API}/api/voice-copilot/full-context`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ brand_id: brandId, filters })
      });
      const data = await res.json();
      if (data.success) setBrandContext(data.context);
    } catch (err) {
      console.error('Context fetch error:', err);
    } finally {
      setContextLoading(false);
    }
  }, [brandId, filters]);

  useEffect(() => {
    if (isOpen && brandId && brandId !== prevBrandIdRef.current) {
      prevBrandIdRef.current = brandId;
      fetchBrandContext();
    }
  }, [isOpen, brandId, fetchBrandContext]);

  // Welcome message
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      const welcome = brandName 
        ? `Hi! I'm your Fundle Copilot — a CRM and Loyalty expert. I have full access to ${brandName}'s live data across all dashboards. Ask me anything about the numbers, or switch to voice mode for a hands-free conversation.`
        : `Hi! I'm your Fundle Copilot — a CRM and Loyalty expert at Fundle. Select a brand and I'll have instant access to all its live analytics.`;
      setMessages([{ role: 'assistant', content: welcome, timestamp: new Date() }]);
      if (brandId) fetchBrandContext();
    }
  }, [isOpen]);

  // === VOICE ACTIVITY DETECTION (VAD) ===
  const getAudioLevel = useCallback(() => {
    if (!analyserRef.current) return -100;
    const data = new Float32Array(analyserRef.current.fftSize);
    analyserRef.current.getFloatTimeDomainData(data);
    let sum = 0;
    for (let i = 0; i < data.length; i++) sum += data[i] * data[i];
    const rms = Math.sqrt(sum / data.length);
    return 20 * Math.log10(Math.max(rms, 1e-10));
  }, []);

  const monitorAudio = useCallback(() => {
    const level = getAudioLevel();
    const normalized = Math.max(0, Math.min(1, (level + 60) / 50));
    setVoiceLevel(normalized);

    if (level > VAD_CONFIG.silenceThreshold) {
      // Sound detected — reset silence timers
      hasSpokenRef.current = true;
      clearTimeout(silenceTimerRef.current);
      clearTimeout(promptTimerRef.current);
      
      // Set new silence timer
      silenceTimerRef.current = setTimeout(() => {
        if (isVoiceModeRef.current && !isSpeakingRef.current && !isProcessingRef.current && hasSpokenRef.current) {
          const elapsed = Date.now() - (recordStartRef.current || Date.now());
          if (elapsed >= VAD_CONFIG.minRecordDuration) {
            stopAndSubmitRecording();
          }
        }
      }, VAD_CONFIG.silenceDuration);
    }

    if (isVoiceModeRef.current && !isSpeakingRef.current && !isProcessingRef.current) {
      vadFrameRef.current = requestAnimationFrame(monitorAudio);
    }
  }, [getAudioLevel]);

  const startListening = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true } 
      });
      streamRef.current = stream;

      // Set up audio analysis for VAD
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      const source = audioContextRef.current.createMediaStreamSource(stream);
      analyserRef.current = audioContextRef.current.createAnalyser();
      analyserRef.current.fftSize = 2048;
      source.connect(analyserRef.current);

      // Start recording
      mediaRecorderRef.current = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      audioChunksRef.current = [];
      hasSpokenRef.current = false;
      recordStartRef.current = Date.now();

      mediaRecorderRef.current.ondataavailable = (e) => {
        if (e.data.size > 0) audioChunksRef.current.push(e.data);
      };

      mediaRecorderRef.current.start(250); // collect chunks every 250ms
      setIsListening(true);

      // Start VAD monitoring
      vadFrameRef.current = requestAnimationFrame(monitorAudio);

      // Prompt timeout — if user doesn't speak at all
      promptTimerRef.current = setTimeout(() => {
        if (isVoiceModeRef.current && !hasSpokenRef.current && !isProcessingRef.current && !isSpeakingRef.current) {
          addAssistantMessage("I'm listening... go ahead and ask me anything about the data.");
        }
      }, VAD_CONFIG.promptTimeout);

    } catch (error) {
      console.error('Mic error:', error);
      toast.error('Microphone access needed for voice mode. Please allow microphone access.');
      setIsVoiceMode(false);
    }
  }, [monitorAudio]);

  const stopAndSubmitRecording = useCallback(() => {
    clearTimeout(silenceTimerRef.current);
    clearTimeout(promptTimerRef.current);
    cancelAnimationFrame(vadFrameRef.current);
    setIsListening(false);

    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        if (audioBlob.size > 1000 && hasSpokenRef.current) {
          await sendMessage(null, audioBlob);
        } else if (isVoiceModeRef.current) {
          // Too short / no speech — restart listening
          startListening();
        }
      };
      mediaRecorderRef.current.stop();
    }
  }, []);

  const stopListeningCleanup = useCallback(() => {
    clearTimeout(silenceTimerRef.current);
    clearTimeout(promptTimerRef.current);
    cancelAnimationFrame(vadFrameRef.current);
    setIsListening(false);
    setVoiceLevel(0);

    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.onstop = null;
      mediaRecorderRef.current.stop();
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
      audioContextRef.current.close().catch(() => {});
      audioContextRef.current = null;
    }
  }, []);

  // === VOICE MODE CONTROL ===
  const enterVoiceMode = useCallback(async () => {
    setIsVoiceMode(true);
    isVoiceModeRef.current = true;
    
    // Add voice mode message
    if (brandName) {
      addAssistantMessage(`Voice mode activated. I'm your Fundle Copilot with full access to ${brandName}'s data. Just start talking — I'll listen and respond naturally.`);
    } else {
      addAssistantMessage("Voice mode activated. Select a brand and start talking — I'm listening.");
    }
    
    await startListening();
  }, [brandName, startListening]);

  const exitVoiceMode = useCallback(() => {
    setIsVoiceMode(false);
    isVoiceModeRef.current = false;
    stopListeningCleanup();
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    setIsSpeaking(false);
  }, [stopListeningCleanup]);

  // Auto-restart listening after AI finishes speaking
  const onAudioEnded = useCallback(() => {
    setIsSpeaking(false);
    isSpeakingRef.current = false;
    
    // In voice mode, automatically start listening again
    if (isVoiceModeRef.current && !isProcessingRef.current) {
      setTimeout(() => {
        if (isVoiceModeRef.current) startListening();
      }, 300);
    }
  }, [startListening]);

  // Handle navigation
  const handleNavigation = useCallback((navigateTo) => {
    if (navigateTo && onNavigate) {
      onNavigate(navigateTo);
    }
  }, [onNavigate]);

  const addAssistantMessage = (content) => {
    setMessages(prev => [...prev, { role: 'assistant', content, timestamp: new Date() }]);
  };

  // === SEND MESSAGE (text or audio) ===
  const sendMessage = useCallback(async (text, audioBlob) => {
    if (!text && !audioBlob) return;
    setIsProcessing(true);
    isProcessingRef.current = true;

    const userMsg = {
      role: 'user',
      content: audioBlob ? '' : text,
      isVoice: !!audioBlob,
      isLoading: !!audioBlob,
      timestamp: new Date()
    };
    setMessages(prev => [...prev, userMsg]);

    try {
      const body = {
        dashboard_type: dashboardType,
        brand_id: brandId,
        brand_name: brandName,
        filters,
        session_id: sessionId,
        want_audio: isVoiceMode || !isMuted,
        brand_context: brandContext
      };

      if (audioBlob) {
        const reader = new FileReader();
        const base64Audio = await new Promise((resolve, reject) => {
          reader.onloadend = () => resolve(reader.result.split(',')[1]);
          reader.onerror = reject;
          reader.readAsDataURL(audioBlob);
        });
        body.audio_base64 = base64Audio;
      } else {
        body.question = text;
      }

      const response = await fetch(`${API}/api/voice-copilot/chat`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });

      const data = await response.json();

      if (data.success) {
        setMessages(prev => {
          const updated = [...prev];
          if (audioBlob) {
            const idx = updated.findLastIndex(m => m.role === 'user' && m.isLoading);
            if (idx >= 0) {
              updated[idx] = { role: 'user', content: data.question || '...', isVoice: true, timestamp: new Date() };
            }
          }
          return [...updated, { role: 'assistant', content: data.text_response, timestamp: new Date() }];
        });

        if (data.navigate_to) handleNavigation(data.navigate_to);

        // Play audio — after it ends, onAudioEnded will restart listening
        if (data.audio_base64) {
          playAudio(data.audio_base64);
        } else if (isVoiceModeRef.current) {
          // No audio to play, restart listening directly
          setTimeout(() => {
            if (isVoiceModeRef.current) startListening();
          }, 500);
        }
      } else {
        throw new Error(data.error || 'Failed');
      }
    } catch (error) {
      console.error('Chat error:', error);
      setMessages(prev => {
        const filtered = prev.filter(m => !m.isLoading);
        return [...filtered, { role: 'assistant', content: "Something went wrong. Let me try again — go ahead and ask.", timestamp: new Date() }];
      });
      // Restart listening in voice mode even on error
      if (isVoiceModeRef.current) {
        setTimeout(() => startListening(), 1000);
      }
    } finally {
      setIsProcessing(false);
      isProcessingRef.current = false;
    }
  }, [dashboardType, brandId, brandName, filters, sessionId, isMuted, brandContext, isVoiceMode, handleNavigation, startListening]);

  const handleSendText = useCallback(() => {
    const text = inputText.trim();
    if (!text) return;
    setInputText('');
    sendMessage(text, null);
  }, [inputText, sendMessage]);

  const handleQuickAction = useCallback((text) => {
    sendMessage(text, null);
  }, [sendMessage]);

  const playAudio = (base64Audio) => {
    if (audioRef.current) {
      audioRef.current.src = `data:audio/mpeg;base64,${base64Audio}`;
      audioRef.current.play().catch(() => {});
      setIsSpeaking(true);
      isSpeakingRef.current = true;
    }
  };

  const stopSpeaking = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    setIsSpeaking(false);
    isSpeakingRef.current = false;
    // After stopping, restart listening in voice mode
    if (isVoiceModeRef.current) {
      setTimeout(() => startListening(), 300);
    }
  };

  const resetChat = () => {
    exitVoiceMode();
    setMessages([]);
    setBrandContext('');
    prevBrandIdRef.current = null;
    setIsOpen(false);
    setTimeout(() => setIsOpen(true), 100);
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopListeningCleanup();
      cancelAnimationFrame(vadFrameRef.current);
    };
  }, [stopListeningCleanup]);

  const quickActions = brandId ? [
    `Overview of ${brandName || 'this brand'}`,
    "What needs immediate attention?",
    "How's the repeat rate vs industry?",
    "Show me store performance",
    "Explain the points economics",
    "Which customers are at churn risk?"
  ] : [
    "What can you help me with?",
    "How do I read the dashboards?"
  ];

  // === RENDER ===
  if (!isOpen) {
    return createPortal(
      <button
        onClick={() => setIsOpen(true)}
        data-testid="voice-copilot-toggle"
        className="fixed bottom-6 right-6 z-50 h-14 w-14 rounded-full shadow-xl flex items-center justify-center transition-all hover:scale-110 active:scale-95 group"
        style={{ background: 'linear-gradient(135deg, #4f46e5, #7c3aed)' }}
      >
        <Sparkles className="w-6 h-6 text-white group-hover:animate-pulse" />
      </button>,
      document.body
    );
  }

  return createPortal(
    <>
      <audio ref={audioRef} onEnded={onAudioEnded} />

      <div 
        data-testid="voice-copilot-panel"
        className="fixed right-6 bottom-6 z-50 w-[420px] bg-[#0f1117] rounded-2xl border border-indigo-500/20 shadow-2xl flex flex-col transition-all duration-300"
        style={{ maxHeight: isExpanded ? '620px' : '56px', height: isExpanded ? '620px' : '56px' }}
      >
        {/* Header */}
        <div 
          className="px-4 py-3 border-b border-white/5 flex items-center justify-between shrink-0"
          data-testid="voice-copilot-header"
        >
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => setIsExpanded(!isExpanded)}>
            <div className={cn(
              "p-1.5 rounded-lg transition-all",
              isVoiceMode && isListening ? "bg-green-500/20 animate-pulse" : 
              isSpeaking ? "bg-blue-500/20 animate-pulse" : 
              contextLoading ? "bg-amber-500/20" : "bg-indigo-500/20"
            )}>
              {contextLoading ? (
                <Loader2 className="w-5 h-5 text-amber-400 animate-spin" />
              ) : isVoiceMode ? (
                <Mic className={cn("w-5 h-5", isListening ? "text-green-400" : isSpeaking ? "text-blue-400" : "text-indigo-400")} />
              ) : (
                <Sparkles className="w-5 h-5 text-indigo-400" />
              )}
            </div>
            <div>
              <h3 className="font-semibold text-white text-sm flex items-center gap-2">
                Fundle Copilot
                {isVoiceMode && isListening && (
                  <span className="text-[10px] text-green-400 font-normal flex items-center gap-1">
                    <span className="w-1.5 h-1.5 bg-green-400 rounded-full animate-pulse" />
                    Listening
                  </span>
                )}
                {isSpeaking && <span className="text-[10px] text-blue-400 font-normal">Speaking</span>}
                {isProcessing && <span className="text-[10px] text-amber-400 font-normal">Thinking...</span>}
              </h3>
              <p className="text-[11px] text-gray-500 truncate max-w-[180px]">
                {brandName ? `${brandName} • CRM & Loyalty Expert` : 'CRM & Loyalty Expert'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-0.5">
            {isVoiceMode && isSpeaking && (
              <Button 
                variant="ghost" size="icon"
                onClick={stopSpeaking}
                className="text-red-400 hover:text-red-300 h-7 w-7"
                data-testid="voice-copilot-stop-speaking"
              >
                <StopCircle className="w-3.5 h-3.5" />
              </Button>
            )}
            <Button 
              variant="ghost" size="icon" 
              onClick={(e) => { e.stopPropagation(); setIsMuted(!isMuted); }}
              className="text-gray-500 hover:text-white h-7 w-7"
              data-testid="voice-copilot-mute-btn"
            >
              {isMuted ? <VolumeX className="w-3.5 h-3.5" /> : <Volume2 className="w-3.5 h-3.5" />}
            </Button>
            <Button 
              variant="ghost" size="icon"
              onClick={(e) => { e.stopPropagation(); resetChat(); }}
              className="text-gray-500 hover:text-white h-7 w-7"
              data-testid="voice-copilot-reset-btn"
            >
              <RotateCcw className="w-3.5 h-3.5" />
            </Button>
            <Button 
              variant="ghost" size="icon"
              onClick={() => setIsExpanded(!isExpanded)}
              className="text-gray-500 hover:text-white h-7 w-7"
            >
              {isExpanded ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronUp className="w-3.5 h-3.5" />}
            </Button>
            <Button 
              variant="ghost" size="icon"
              onClick={() => { exitVoiceMode(); setIsOpen(false); }}
              className="text-gray-500 hover:text-white h-7 w-7"
              data-testid="voice-copilot-close-btn"
            >
              <X className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>

        {isExpanded && (
          <>
            {/* Messages */}
            <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3" data-testid="voice-copilot-messages">
              {messages.map((msg, idx) => (
                <div 
                  key={idx}
                  className={cn("flex gap-2.5", msg.role === 'user' ? "justify-end" : "justify-start")}
                >
                  {msg.role === 'assistant' && (
                    <div className="w-7 h-7 rounded-lg bg-indigo-500/15 flex items-center justify-center shrink-0 mt-0.5">
                      <Sparkles className="w-3.5 h-3.5 text-indigo-400" />
                    </div>
                  )}
                  <div className={cn(
                    "max-w-[82%] rounded-2xl px-3.5 py-2.5",
                    msg.role === 'user' 
                      ? "bg-indigo-600 text-white rounded-br-md" 
                      : "bg-[#1a1d28] text-gray-200 rounded-bl-md border border-white/5"
                  )}>
                    <p className="text-[13px] leading-relaxed whitespace-pre-wrap">
                      {msg.isLoading ? (
                        <span className="flex items-center gap-2 text-gray-400">
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          Listening...
                        </span>
                      ) : msg.content}
                    </p>
                    {msg.isVoice && !msg.isLoading && (
                      <span className="text-[10px] text-indigo-300/60 flex items-center gap-1 mt-1">
                        <Mic className="w-2.5 h-2.5" /> voice
                      </span>
                    )}
                  </div>
                  {msg.role === 'user' && !msg.isLoading && (
                    <div className="w-7 h-7 rounded-lg bg-indigo-600/40 flex items-center justify-center shrink-0 mt-0.5">
                      <User className="w-3.5 h-3.5 text-indigo-200" />
                    </div>
                  )}
                </div>
              ))}

              {/* Thinking indicator */}
              {isProcessing && messages[messages.length - 1]?.role === 'user' && (
                <div className="flex gap-2.5">
                  <div className="w-7 h-7 rounded-lg bg-indigo-500/15 flex items-center justify-center shrink-0">
                    <Sparkles className="w-3.5 h-3.5 text-indigo-400 animate-pulse" />
                  </div>
                  <div className="bg-[#1a1d28] rounded-2xl rounded-bl-md px-4 py-3 border border-white/5">
                    <div className="flex gap-1.5">
                      <div className="w-2 h-2 bg-indigo-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                      <div className="w-2 h-2 bg-indigo-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                      <div className="w-2 h-2 bg-indigo-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                    </div>
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>

            {/* Quick Actions */}
            {messages.length <= 2 && !isProcessing && !isVoiceMode && (
              <div className="px-4 pb-2 shrink-0">
                <p className="text-[10px] text-gray-600 mb-1.5 uppercase tracking-wider">Ask me</p>
                <div className="flex flex-wrap gap-1.5">
                  {quickActions.map((action, idx) => (
                    <button
                      key={idx}
                      data-testid={`voice-copilot-quick-${idx}`}
                      onClick={() => handleQuickAction(action)}
                      disabled={isProcessing}
                      className="text-[11px] px-2.5 py-1.5 rounded-full bg-[#1a1d28] text-gray-400 hover:text-white hover:bg-indigo-600/20 border border-white/5 transition-all"
                    >
                      {action}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Voice Mode Active Indicator */}
            {isVoiceMode && (
              <div className="px-4 pb-2 shrink-0">
                <div className="flex items-center justify-center gap-3 py-3">
                  {/* Audio visualizer */}
                  <div className="flex items-center gap-[3px] h-8">
                    {[...Array(12)].map((_, i) => (
                      <div
                        key={i}
                        className={cn(
                          "w-[3px] rounded-full transition-all duration-75",
                          isListening ? "bg-green-400" : isSpeaking ? "bg-blue-400" : "bg-gray-600"
                        )}
                        style={{
                          height: isListening 
                            ? `${Math.max(4, voiceLevel * 32 * (0.5 + Math.random() * 0.5))}px` 
                            : isSpeaking 
                              ? `${4 + Math.random() * 20}px`
                              : '4px',
                          transition: 'height 75ms ease'
                        }}
                      />
                    ))}
                  </div>
                  <span className={cn(
                    "text-xs font-medium",
                    isListening ? "text-green-400" : isSpeaking ? "text-blue-400" : isProcessing ? "text-amber-400" : "text-gray-500"
                  )}>
                    {isListening ? "Listening..." : isSpeaking ? "Speaking..." : isProcessing ? "Thinking..." : "Ready"}
                  </span>
                </div>
              </div>
            )}

            {/* Input Area */}
            <div className="p-3 border-t border-white/5 shrink-0">
              {isVoiceMode ? (
                /* Voice mode: single button to exit */
                <div className="flex items-center justify-center gap-3">
                  <Button
                    onClick={exitVoiceMode}
                    data-testid="voice-copilot-exit-voice"
                    className="rounded-full bg-red-500/20 hover:bg-red-500/30 text-red-400 gap-2 px-6"
                  >
                    <PhoneOff className="w-4 h-4" />
                    End Voice Mode
                  </Button>
                </div>
              ) : (
                /* Text mode with voice toggle */
                <div className="flex items-center gap-2">
                  <Button
                    onClick={enterVoiceMode}
                    disabled={isProcessing}
                    data-testid="voice-copilot-start-voice"
                    className="h-9 w-9 rounded-full bg-green-600 hover:bg-green-500 shrink-0 transition-all"
                    title="Start voice conversation"
                  >
                    <Phone className="w-3.5 h-3.5 text-white" />
                  </Button>

                  <input
                    type="text"
                    value={inputText}
                    onChange={(e) => setInputText(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSendText()}
                    placeholder={brandId ? "Ask about the data..." : "Select a brand first"}
                    disabled={isProcessing}
                    data-testid="voice-copilot-input"
                    className="flex-1 bg-[#1a1d28] text-white text-sm rounded-full px-4 py-2 border border-white/10 focus:border-indigo-500 focus:outline-none placeholder:text-gray-600"
                  />

                  <Button
                    onClick={handleSendText}
                    disabled={!inputText.trim() || isProcessing}
                    data-testid="voice-copilot-send-btn"
                    className="h-9 w-9 rounded-full bg-indigo-600 hover:bg-indigo-700 disabled:opacity-30 shrink-0"
                  >
                    {isProcessing ? (
                      <Loader2 className="w-3.5 h-3.5 text-white animate-spin" />
                    ) : (
                      <Send className="w-3.5 h-3.5 text-white" />
                    )}
                  </Button>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </>,
    document.body
  );
}
